import React from "react";
import WarningIcon from "../../../../../assets/svg/warning-icon";
import ChevronRightIcon from "../../../../../assets/svg/chevron-right-icon";
import CalendarIcon from "../../../../../assets/svg/calendar-icon";
import HashtagIcon from "../../../../../assets/svg/hashtag-icon";
import { Avatar } from "../../../../../components/Avatar/Avatar";
import { ExistingLeadData } from "../utils/types";
import { formatDate } from "../utils/functions";

export type { ExistingLeadData };

interface LeadExistsWarningProps {
  leadData: ExistingLeadData;
  fieldType: "email" | "phone" | "whatsapp";
  onViewLead: (leadId: string) => void;
  onUseDifferent: () => void;
}

const LeadExistsWarning: React.FC<LeadExistsWarningProps> = ({
  leadData,
  fieldType,
  onViewLead,
  onUseDifferent,
}) => {
  const fieldLabel =
    fieldType === "email"
      ? "email"
      : fieldType === "phone"
        ? "number"
        : "number";

  return (
    <div className="flex flex-col gap-[14px] items-start justify-center overflow-clip p-[12px] rounded-[8px] w-full border bg-tag-yellow-bg dark:bg-tag-yellow-bg-dk border-card-yellow-stroke dark:border-card-yellow-stroke-dk">
      <div className="flex flex-col gap-[6px] items-start w-full">
        <div className="flex gap-[4px] items-center w-full">
          <WarningIcon className="w-[14px] h-[14px] text-rating-yellow dark:text-rating-yellow-dk shrink-0" />
          <span className="font-medium text-md leading-none text-verba-indigo dark:text-verba-indigo-dk">
            This {fieldLabel} is already linked to a lead
          </span>
        </div>
        <p className="text-[10px] leading-[1.5] text-data dark:text-data-dk w-full">
          Continuing will create a duplicate record. Open the existing lead to
          log an activity instead.
        </p>
      </div>

      <div className="bg-pure-white dark:bg-pure-white-dk flex flex-col gap-[8px] items-start px-[8px] py-[10px] rounded-[8px] w-full">
        <div className="flex gap-[8px] items-center w-full">
          <div className="flex flex-1 gap-[5px] items-center min-w-0">
            <Avatar size={24} text={leadData.name} className="shrink-0" />
            <p className="flex-1 font-medium text-md leading-[1.5] text-heading-text dark:text-heading-text-dk overflow-hidden text-ellipsis whitespace-nowrap min-w-0">
              {leadData.name}
            </p>
          </div>
          {leadData.stage && (
            <div className="bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk flex gap-[4px] h-[20px] items-center px-[6px] py-[2px] rounded-[6px] shrink-0">
              <div
                className="w-[8px] h-[8px] rounded-full shrink-0"
                style={{
                  backgroundColor:
                    leadData.stage_colour || "rgba(247,104,8,0.8)",
                }}
              />
              <span className="font-semibold text-[10px] leading-[18px] text-verba-indigo dark:text-verba-indigo-dk uppercase whitespace-nowrap">
                {leadData.stage}
              </span>
            </div>
          )}
        </div>

        <div className="flex gap-[8px] items-center">
          <div className="flex gap-[2px] items-center">
            <HashtagIcon className="w-[14px] h-[14px] text-analytics-gray dark:text-analytics-gray-dk" />
            <span className="font-medium text-xs leading-[1.5] text-analytics-gray dark:text-analytics-gray-dk whitespace-nowrap">
              #{leadData.id}
            </span>
          </div>
          <div className="w-0 h-[14px] border-l border-border-gray dark:border-border-gray-dk" />
          <div className="flex gap-[2px] items-center">
            <CalendarIcon className="w-[14px] h-[14px] text-analytics-gray dark:text-analytics-gray-dk" />
            <span className="font-medium text-xs leading-[1.5] text-analytics-gray dark:text-analytics-gray-dk whitespace-nowrap">
              {formatDate(leadData.created_at)}
            </span>
          </div>
        </div>
      </div>

      <div className="flex gap-[14px] items-center w-full">
        <button
          type="button"
          onClick={() => onViewLead(leadData.id)}
          className="bg-verba-indigo dark:bg-verba-indigo-dk drop-shadow-[1px_1px_2px_rgba(10,13,18,0.08)] flex gap-[2px] h-[26px] items-center justify-center px-[12px] py-[4px] rounded-[6px] shrink-0"
        >
          <span className="font-semibold text-[10px] leading-[1.5] text-pure-white dark:text-pure-white-dk">
            View Lead
          </span>
          <ChevronRightIcon className="w-[12px] h-[12px] text-pure-white dark:text-pure-white-dk" />
        </button>
        <button
          type="button"
          onClick={onUseDifferent}
          className="font-medium text-md leading-normal text-verba-blue dark:text-verba-blue-dk whitespace-nowrap"
        >
          Use a different {fieldLabel}
        </button>
      </div>
    </div>
  );
};

export default LeadExistsWarning;
